import React from 'react';
import {useEnvironment, useTranslation} from '@wix/yoshi-flow-editor';
import {TagSmall} from '@wix/wix-ui-icons-common';
import {useControllerProps} from '../../../domain/controllers/ControllerContext';
import s from './Discounts.scss';
import {TotalUpdateSkeleton} from '../../../components/cart/Widget/Components/OrderSummary/SubTotals/TotalUpdateSkeleton';
import {DiscountScope, DiscountSource, DiscountModel} from '../../../domain/models/CartSummary/Discount.model';
import {TotalGroup} from '../../../components/cart/Widget/Components/OrderSummary/SubTotals/TotalGroup/TotalGroup';
import {shorten} from '../../../domain/utils/shorten';

export enum DiscountsDataHook {
  Root = 'Discounts.DiscountsRoot',
  DiscountIcon = 'Discounts.DiscountsIcon',
  DiscountLabel = 'Discounts.DiscountsLabel',
  DiscountValue = 'Discounts.DiscountsDiscountValue',
}

export interface DiscountsProps {
  shouldShowDivider?: boolean;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const Discounts = ({shouldShowDivider}: DiscountsProps) => {
  const {t} = useTranslation();
  const {isRTL} = useEnvironment();
  const {cartModel, cartSummary, isSummaryUpdating, couponAlignmentInCartAndCheckout} = useControllerProps().cartStore;
  const globalDiscounts = cartSummary.globalDiscounts;
  const appliedCoupon = cartModel.coupon;

  const getDiscountRowValueText = (discount?: DiscountModel): string => {
    const isFreeShippingDiscount = couponAlignmentInCartAndCheckout
      ? discount.sourceType === DiscountSource.COUPON && discount.scope === DiscountScope.DELIVERY
      : cartSummary.couponDiscount?.isFreeShippingCoupon;
    const totalAmountOff = couponAlignmentInCartAndCheckout
      ? discount.totalAmountOff
      : cartSummary?.priceSummary?.discount.formattedConvertedAmount;

    if (isFreeShippingDiscount) {
      return t('cart.summary.discount.free_delivery');
    }

    if (isRTL) {
      return `${totalAmountOff}-`;
    }
    return `-${totalAmountOff}`;
  };

  let totals;
  if (couponAlignmentInCartAndCheckout) {
    totals = globalDiscounts.map((discount) => ({
      title: discount.name,
      value: isSummaryUpdating ? () => <TotalUpdateSkeleton /> : getDiscountRowValueText(discount),
      key: discount.name,
      labelPrefix: <TagSmall aria-hidden data-hook={DiscountsDataHook.DiscountIcon} className={s.discountIcon} />,
      className: s.discountRow,
      additionalAttributes: {
        'data-wix-coupon-discount-value': DiscountsDataHook.DiscountValue,
      },
    }));
  } else {
    const couponCode = shorten(appliedCoupon?.code?.toUpperCase(), 23, '...');
    totals = [
      {
        title: couponCode,
        value: isSummaryUpdating ? () => <TotalUpdateSkeleton /> : getDiscountRowValueText(),
        key: couponCode,
        labelPrefix: <TagSmall aria-hidden data-hook={DiscountsDataHook.DiscountIcon} className={s.discountIcon} />,
        className: s.discountRow,
        additionalAttributes: {
          'data-wix-coupon-discount-value': DiscountsDataHook.DiscountValue,
        },
      },
    ];
  }

  const shouldShowBottomDivider = shouldShowDivider && totals.length > 1;

  return (
    <div className={shouldShowBottomDivider ? s.bottomDivider : undefined}>
      <TotalGroup dataHook={DiscountsDataHook.Root} totals={totals} title={t('cart.Discounts')} />
    </div>
  );
};
